import Vue from 'vue'
import Vuex from 'vuex'
import { menuTree, findParentIndex, getChildrenByIndex } from '@/config/menu'
import { getToken, setToken, removeToken } from '@/utils/auth'
import { vendors as seedVendors, products as seedProducts, seedLogs, reportOrders as seedReportOrders, reverseApps as seedReverseApps, shipOrders as seedShipOrders, qualifications as seedQualifications, contracts as seedContracts, contractTemplates as seedContractTemplates, shutdowns as seedShutdowns, invoices as seedInvoices, aftersales as seedAftersales, biddings as seedBiddings, inquiries as seedInquiries, messages as seedMessages, roles as seedRoles } from '@/mock/data'

function nowStr() {
  const d = new Date()
  const p = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

Vue.use(Vuex)

const VISITED_VIEWS_KEY = 'manager_visited_views'

function loadVisitedViews() {
  try {
    const raw = window.sessionStorage.getItem(VISITED_VIEWS_KEY)
    return raw ? JSON.parse(raw) : []
  } catch (e) {
    return []
  }
}

function saveVisitedViews(list) {
  try {
    window.sessionStorage.setItem(VISITED_VIEWS_KEY, JSON.stringify(list))
  } catch (e) {
    // ignore storage error
  }
}

export default new Vuex.Store({
  state: {
    // 一级菜单数据
    menuList: menuTree,
    // 供应商档案（列表用，消息发送等模块作为收件人来源）
    vendors: JSON.parse(JSON.stringify(seedVendors)),
    // 当前激活的一级菜单 index (默认 0 = 工作台)
    activeMenuIndex: 0,
    // 当前二级菜单列表
    childMenuList: menuTree[0].children || [],
    // 二级菜单是否收起（columns 模式下无子菜单或只有一个时自动收起）
    isCollapse: false,
    // 登录 Token
    token: getToken(),
    // 用户信息（mock）
    userInfo: {
      name: '平台管理员',
      avatar: '',
      phone: '',
      company: '安徽亿佳供应链集团',
    },
    // 已打开的页面标签（顶部 TagsView），刷新后从 sessionStorage 恢复
    visitedViews: loadVisitedViews(),
    // 商品（管理端商品中心数据源：审核/下架/重提后状态在此更新，形成「提交→审核→通过→下架」闭环）
    products: JSON.parse(JSON.stringify(seedProducts)),
    // 报备订单（管理端报备审核台数据源：审核通过后状态在此更新）
    reportOrders: JSON.parse(JSON.stringify(seedReportOrders)),
    // 反采申请（管理端反采审核台数据源：审核通过后状态在此更新）
    reverseApps: JSON.parse(JSON.stringify(seedReverseApps)),
    // 发货单（管理端发货单管理数据源：妥投/拒收处理后状态在此更新）
    shipOrders: JSON.parse(JSON.stringify(seedShipOrders)),
    // 资质/授权审核（数据源）
    qualifications: JSON.parse(JSON.stringify(seedQualifications)),
    // 合同管理（数据源）
    contracts: JSON.parse(JSON.stringify(seedContracts)),
    // 合同模板库（数据源）
    contractTemplates: JSON.parse(JSON.stringify(seedContractTemplates)),
    // 供应商关停审批（数据源）
    shutdowns: JSON.parse(JSON.stringify(seedShutdowns)),
    // 发票管理（数据源）
    invoices: JSON.parse(JSON.stringify(seedInvoices)),
    // 售后服务管理（数据源）
    aftersales: JSON.parse(JSON.stringify(seedAftersales)),
    // 采购竞价管理（数据源）
    biddings: JSON.parse(JSON.stringify(seedBiddings)),
    // 询价单管理（数据源）
    inquiries: JSON.parse(JSON.stringify(seedInquiries)),
    // 消息中心（数据源）
    messages: JSON.parse(JSON.stringify(seedMessages)),
    // 角色权限（数据源）
    roles: JSON.parse(JSON.stringify(seedRoles)),
    // 操作日志（全平台审计追踪，各模块审核/治理动作均写入此处）
    logs: JSON.parse(JSON.stringify(seedLogs)),
    logSeq: 0,
  },

  getters: {
    currentMenuTitle: (state) => {
      const item = state.menuList[state.activeMenuIndex]
      return item ? item.title : ''
    },
    hasChildren: (state) => {
      return state.childMenuList && state.childMenuList.length > 0
    },
  },

  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
    },

    SET_USER_INFO(state, info) {
      state.userInfo = { ...state.userInfo, ...info }
    },

    /** 切换一级菜单 */
    SET_ACTIVE_MENU_INDEX(state, index) {
      state.activeMenuIndex = index
      state.childMenuList = getChildrenByIndex(state.menuList, index)
      // 无子菜单或只有1个时自动收起
      const children = state.childMenuList
      state.isCollapse = !children || children.length <= 1
    },

    /** 设置子菜单列表 */
    SET_CHILD_MENU_LIST(state, list) {
      state.childMenuList = list
      state.isCollapse = !list || list.length <= 1
    },

    /** 切换折叠 */
    TOGGLE_COLLAPSE(state) {
      state.isCollapse = !state.isCollapse
    },

    /** TagsView：添加已访问视图 */
    ADD_VISITED_VIEW(state, view) {
      if (state.visitedViews.some(v => v.path === view.path)) return
      state.visitedViews.push({
        path: view.path,
        title: view.meta.title || '未命名',
        fullPath: view.fullPath || view.path,
      })
      saveVisitedViews(state.visitedViews)
    },

    /** TagsView：删除指定视图 */
    DEL_VISITED_VIEW(state, view) {
      for (const [i, v] of state.visitedViews.entries()) {
        if (v.path === view.path) {
          state.visitedViews.splice(i, 1)
          break
        }
      }
      saveVisitedViews(state.visitedViews)
    },

    /** TagsView：删除其他视图 */
    DEL_OTHERS_VISITED_VIEWS(state, view) {
      state.visitedViews = state.visitedViews.filter(v => {
        return v.path === view.path || (v.meta && v.meta.affix)
      })
      saveVisitedViews(state.visitedViews)
    },

    /** TagsView：删除全部视图 */
    DEL_ALL_VISITED_VIEWS(state) {
      state.visitedViews = []
      saveVisitedViews(state.visitedViews)
    },

    /** 商品：更新审核状态（通过/驳回/打回重提/下架） */
    SET_PRODUCT_STATUS(state, { id, status, reason }) {
      const p = state.products.find(x => x.id === Number(id))
      if (p) {
        p.auditStatus = status
        if (reason !== undefined) p.rejectReason = reason
        p.lastAuditTime = nowStr()
      }
    },

    /** 操作日志：写入一条记录（unshift 置顶） */
    ADD_LOG(state, log) {
      state.logSeq += 1
      state.logs.unshift({
        id: state.logSeq,
        time: log.time || nowStr(),
        module: log.module || '系统',
        action: log.action || '',
        target: log.target || '',
        operator: log.operator || '平台管理员',
        detail: log.detail || '',
      })
    },

    /** 报备订单：更新审核状态（通过/驳回） */
    SET_REPORT_STATUS(state, { id, status, reason }) {
      const r = state.reportOrders.find(x => x.id === Number(id))
      if (r) {
        r.reportStatus = status
        r.auditStatus = status
        if (reason !== undefined) r.rejectReason = reason
        r.auditTime = nowStr()
      }
    },

    /** 反采申请：更新审核状态（通过/驳回） */
    SET_REVERSE_STATUS(state, { id, status, reason }) {
      const r = state.reverseApps.find(x => x.id === Number(id))
      if (r) {
        r.status = status
        if (reason !== undefined) r.rejectReason = reason
        r.auditTime = nowStr()
      }
    },

    /** 发货单：更新妥投/拒收处理状态 */
    SET_SHIP_STATUS(state, { id, deliveredStatus, rejectStatus }) {
      const s = state.shipOrders.find(x => x.id === Number(id))
      if (s) {
        if (deliveredStatus !== undefined) s.deliveredStatus = deliveredStatus
        if (deliveredStatus === '已妥投') { s.status = '已妥投'; s.deliveredTime = nowStr() }
        if (rejectStatus !== undefined) s.rejectStatus = rejectStatus
        if (rejectStatus === '已拒收') s.status = '已拒收'
      }
    },

    /** 资质/授权审核：更新审核状态（通过/驳回） */
    SET_QUALIFICATION_STATUS(state, { id, status, reason }) {
      const q = state.qualifications.find(x => x.id === Number(id))
      if (q) {
        q.auditStatus = status
        if (reason !== undefined) q.remark = reason
        q.updateTime = nowStr()
      }
    },

    /** 合同管理：新建/编辑合同草稿 */
    UPSERT_CONTRACT(state, contract) {
      if (contract.id) {
        const idx = state.contracts.findIndex(x => x.id === Number(contract.id))
        if (idx >= 0) { state.contracts.splice(idx, 1, { ...state.contracts[idx], ...contract }); return }
      }
      const maxId = state.contracts.reduce((mx, c) => Math.max(mx, Number(c.id) || 0), 0)
      state.contracts.unshift({ id: maxId + 1, signStatus: '未发起', contractStatus: '草稿', createdAt: nowStr(), ...contract })
    },
    /** 合同：发起电子签署（写入任务、签署链接、厂商） */
    SEND_CONTRACT_SIGN(state, { id, taskId, signUrl, provider }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) {
        c.signStatus = '签署中'
        c.contractStatus = '签署中'
        c.signTaskId = taskId
        c.signUrl = signUrl
        c.esignProvider = provider
        c.initiatedAt = nowStr()
      }
    },
    /** 合同：签署完成（落章、生成签署件） */
    COMPLETE_CONTRACT_SIGN(state, { id, signedFileUrl, signedAt, signer, doubleStamp }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) {
        c.signStatus = '已签署完成'
        c.contractStatus = '生效中'
        if (c.signType === 2 || doubleStamp) c.doubleStampContractUrl = signedFileUrl
        else c.stampContractUrl = signedFileUrl
        c.signedAt = signedAt || nowStr()
        c.signedBy = signer || ''
        c.lastExpireReminder = ''
        c.lastPaymentReminder = ''
      }
    },
    /** 合同：拒签 */
    REJECT_CONTRACT_SIGN(state, { id, reason }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) { c.signStatus = '已拒签'; c.contractStatus = '待签署'; c.rejectReason = reason || '' }
    },
    /** 合同：历史合同上传补录 */
    UPLOAD_CONTRACT_HISTORY(state, { id, importFile, fields }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) {
        c.isHistory = true
        c.importFile = importFile || c.importFile
        if (fields) Object.assign(c, fields)
        if (c.contractStatus === '草稿' || c.contractStatus === '待签署') c.contractStatus = '生效中'
        if (!c.signStatus || c.signStatus === '未发起') c.signStatus = '已签署完成'
        c.signedAt = c.signedAt || fields.signedAt || nowStr()
      }
    },
    /** 合同：续签（延长有效期） */
    RENEW_CONTRACT(state, { id, newEndDate }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) { c.endDate = newEndDate; c.contractStatus = '生效中'; c.lastExpireReminder = '' }
    },
    /** 合同：终止 */
    TERMINATE_CONTRACT(state, { id, reason }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) { c.contractStatus = '已终止'; c.terminateReason = reason || '' }
    },
    /** 合同：归档 */
    ARCHIVE_CONTRACT(state, { id }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) c.contractStatus = '已归档'
    },
    /** 合同：标记某类提醒已发送 */
    SET_CONTRACT_REMINDER_DONE(state, { id, kind }) {
      const c = state.contracts.find(x => x.id === Number(id))
      if (c) { if (kind === 'expire') c.lastExpireReminder = nowStr(); else c.lastPaymentReminder = nowStr() }
    },
    /** 合同模板：新增/编辑 */
    UPSERT_CONTRACT_TEMPLATE(state, tpl) {
      const idx = state.contractTemplates.findIndex(t => t.id === tpl.id)
      if (idx >= 0) state.contractTemplates.splice(idx, 1, { ...state.contractTemplates[idx], ...tpl })
      else state.contractTemplates.push({ id: Date.now(), enabled: true, updatedAt: nowStr(), ...tpl })
    },
    /** 合同模板：启停 */
    TOGGLE_CONTRACT_TEMPLATE(state, id) {
      const t = state.contractTemplates.find(x => x.id === Number(id))
      if (t) t.enabled = !t.enabled
    },

    /** 供应商关停审批：更新审核状态（通过/驳回） */
    SET_SHUTDOWN_STATUS(state, { id, status, reason }) {
      const s = state.shutdowns.find(x => x.id === Number(id))
      if (s) {
        s.status = status
        s.auditTime = nowStr()
        if (reason !== undefined) s.auditReason = reason
      }
    },

    /** 发票管理：更新开票/作废/下载状态 */
    SET_INVOICE_STATUS(state, { id, invoiceStatus, downloadStatus }) {
      const i = state.invoices.find(x => x.id === Number(id))
      if (i) {
        if (invoiceStatus !== undefined) { i.invoiceStatus = invoiceStatus; if (invoiceStatus === '已开票') i.invoiceTime = nowStr() }
        if (downloadStatus !== undefined) i.downloadStatus = downloadStatus
      }
    },

    /** 售后服务管理：更新处理状态（通过/驳回/介入完成） */
    SET_AFTERSALE_STATUS(state, { id, status }) {
      const a = state.aftersales.find(x => x.id === Number(id))
      if (a) {
        a.status = status
        if (status === '已完成') a.finishTime = nowStr()
      }
    },

    /** 采购竞价：认领/分配负责人 */
    SET_BIDDING_OWNER(state, { id, owner }) {
      const b = state.biddings.find(x => x.id === Number(id))
      if (b) b.owner = owner
    },

    /** 询价单：指派供应商 */
    SET_INQUIRY_ASSIGN(state, { id, vendor }) {
      const i = state.inquiries.find(x => x.id === Number(id))
      if (i) { i.assignedVendor = vendor; if (i.status === '待报价') i.status = '已报价' }
    },

    /** 消息中心：标记已读 / 全部已读 */
    SET_MESSAGE_READ(state, { id, all }) {
      if (all) {
        state.messages.forEach(m => { m.read = true })
      } else {
        const m = state.messages.find(x => x.id === Number(id))
        if (m) m.read = true
      }
    },

    /** 消息中心：平台主动发送站内 PM（支持多个收件人，逐条写入） */
    SEND_MESSAGE(state, list) {
      const maxId = state.messages.reduce((mx, m) => Math.max(mx, Number(m.id) || 0), 0)
      let seq = maxId
      list.forEach(m => {
        seq += 1
        state.messages.unshift({
          id: seq,
          title: m.title,
          content: m.content,
          type: m.type || '通知',
          module: m.module || '消息中心',
          recipient: m.recipient || '',
          sender: m.sender || '平台管理员',
          relatedPath: m.relatedPath || '',
          time: nowStr(),
          read: true, // 发送方（平台）视为已读，不计入未读徽章
        })
      })
    },

    /** 角色权限：新增/编辑/删除 */
    UPSERT_ROLE(state, role) {
      const idx = state.roles.findIndex(r => r.id === role.id)
      if (idx >= 0) state.roles.splice(idx, 1, { ...role })
      else state.roles.push({ ...role })
    },
    REMOVE_ROLE(state, id) {
      state.roles = state.roles.filter(r => r.id !== Number(id))
    },

    /** 重置业务数据（登出时调用） */
    RESET_BIZ_DATA(state) {
      state.vendors = JSON.parse(JSON.stringify(seedVendors))
      state.products = JSON.parse(JSON.stringify(seedProducts))
      state.reportOrders = JSON.parse(JSON.stringify(seedReportOrders))
      state.reverseApps = JSON.parse(JSON.stringify(seedReverseApps))
      state.shipOrders = JSON.parse(JSON.stringify(seedShipOrders))
      state.qualifications = JSON.parse(JSON.stringify(seedQualifications))
      state.contracts = JSON.parse(JSON.stringify(seedContracts))
      state.contractTemplates = JSON.parse(JSON.stringify(seedContractTemplates))
      state.shutdowns = JSON.parse(JSON.stringify(seedShutdowns))
      state.invoices = JSON.parse(JSON.stringify(seedInvoices))
      state.aftersales = JSON.parse(JSON.stringify(seedAftersales))
      state.biddings = JSON.parse(JSON.stringify(seedBiddings))
      state.inquiries = JSON.parse(JSON.stringify(seedInquiries))
      state.messages = JSON.parse(JSON.stringify(seedMessages))
      state.roles = JSON.parse(JSON.stringify(seedRoles))
      state.logs = JSON.parse(JSON.stringify(seedLogs))
      state.logSeq = 0
    },
  },

  actions: {
    /**
     * 根据 path 自动设置一级菜单高亮
     * 在路由变化时调用（直接访问 URL / 刷新 / 路由跳转 都要能定位）
     */
    syncMenuFromPath({ commit, state }, path) {
      const index = findParentIndex(state.menuList, path)
      if (index >= 0 && state.activeMenuIndex !== index) {
        commit('SET_ACTIVE_MENU_INDEX', index)
      }
    },

    /** 登录 */
    login({ commit }, userInfo) {
      const token = 'manager-token-' + Date.now()
      setToken(token)
      commit('SET_TOKEN', token)
      commit('SET_USER_INFO', { name: userInfo.username })
      return Promise.resolve()
    },

    /** 登出 */
    logout({ commit }) {
      removeToken()
      commit('SET_TOKEN', '')
      commit('SET_USER_INFO', { name: '', avatar: '', phone: '', company: '' })
      commit('DEL_ALL_VISITED_VIEWS')
      // 重置业务数据到初始态（避免下次登录串数据）
      commit('RESET_BIZ_DATA')
      try {
        window.sessionStorage.removeItem(VISITED_VIEWS_KEY)
      } catch (e) {
        // ignore
      }
      return Promise.resolve()
    },

    /** 追加操作日志 */
    addLog({ commit }, log) {
      commit('ADD_LOG', log)
    },

    /** 添加一个已访问的页面标签 */
    addView({ commit }, view) {
      if (!view || (view.meta && view.meta.hidden)) return
      const exists = this.state.visitedViews.some(v => v.path === view.path)
      if (exists) return
      commit('ADD_VISITED_VIEW', view)
    },

    /** 关闭指定标签 */
    delView({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_VISITED_VIEW', view)
        resolve([...state.visitedViews])
      })
    },

    /** 关闭其他标签 */
    delOthersViews({ commit, state }, view) {
      return new Promise(resolve => {
        commit('DEL_OTHERS_VISITED_VIEWS', view)
        resolve([...state.visitedViews])
      })
    },

    /** 关闭所有标签 */
    delAllViews({ commit, state }) {
      return new Promise(resolve => {
        commit('DEL_ALL_VISITED_VIEWS')
        resolve([...state.visitedViews])
      })
    },
  },
})
